import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextInput from '../../widgets/TextInput';
import NumberInput from '../../widgets/NumberInput';
import SelectInput from '../../widgets/SelectInput';
import { SupplierInvoice } from '../../../models/SupplierInvoice';
import { Supplier } from '../../../models/Supplier';
import { FiscalYear } from '../../../models/FiscalYear';
import { Ledger } from '../../../models/Ledger';
import { BankAccount } from '../../../models/BankAccount';
import { LedgerType } from '../../../models/LedgerType';
import { FaSave, FaTimes, FaTrash, FaBook, FaMoneyBillWave, FaUndo } from 'react-icons/fa';
import AttachmentsPanel from '../../common/Attachments/AttachmentsPanel';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import GenericVerticalTabs, { TabConfig } from '../../common/GenericVerticalTabs/GenericVerticalTabs';
import ModalDialog from '../../common/ModalDialog/ModalDialog';
import './SupplierInvoiceDialog.scss';

interface SupplierInvoiceDialogProps {
    open: boolean;
    invoice: SupplierInvoice | null;
    suppliers: Supplier[];
    fiscalYears: FiscalYear[];
    ledgers: Ledger[];
    bankAccounts: BankAccount[];
    onSave: (invoice: SupplierInvoice) => Promise<SupplierInvoice>;
    onDelete: (invoice: SupplierInvoice) => void;
    onBook: (invoice: SupplierInvoice, costLedgerId: string) => Promise<void>;
    onBookPayment: (invoice: SupplierInvoice, paymentDate: string, bankAccountId: string) => Promise<void>;
    onUnbook: (invoice: SupplierInvoice) => Promise<void>;
    onClose: () => void;
}

const emptyInvoice: SupplierInvoice = {
    id: '',
    supplierId: '',
    fiscalYearId: null,
    invoiceDate: null,
    paymentDate: null,
    deliveryDate: null,
    receiveDate: null,
    paymentDueDate: null,
    reference: '',
    invoiceNumber: '',
    supplierReference: '',
    comments: '',
    invoiceFileAttachmentId: null,
    isPaid: false,
    isVatMoved: false,
    isMissedPayment: false,
    originalInvoiceAmountIncVat: null,
    totalAmountExVat: 0,
    totalAmountIncVat: 0,
    isBooked: false,
    bookedAt: null,
};

const SupplierInvoiceDialog: React.FC<SupplierInvoiceDialogProps> = ({
    open, invoice, suppliers, fiscalYears, ledgers, bankAccounts, onSave, onDelete, onBook, onBookPayment, onUnbook, onClose,
}) => {
    const { t } = useTranslation(['common', 'screens', 'validation']);
    const [data, setData] = useState<SupplierInvoice>(invoice ?? emptyInvoice);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showBookConfirm, setShowBookConfirm] = useState(false);
    const [showBookPaymentConfirm, setShowBookPaymentConfirm] = useState(false);
    const [showUnbookConfirm, setShowUnbookConfirm] = useState(false);
    const [bookPaymentDate, setBookPaymentDate] = useState('');
    const [bookPaymentBankAccountId, setBookPaymentBankAccountId] = useState('');
    const [bookCostLedgerId, setBookCostLedgerId] = useState('');
    const [activeTab, setActiveTab] = useState('general');

    useEffect(() => {
        setData(invoice ?? { ...emptyInvoice });
        setValidationError(null);
        setShowDeleteConfirm(false);
        setShowBookConfirm(false);
        setShowBookPaymentConfirm(false);
        setShowUnbookConfirm(false);
        setBookPaymentDate('');
        setBookPaymentBankAccountId('');
        setBookCostLedgerId('');
        setActiveTab('general');
    }, [invoice, open]);

    if (!open) return null;

    const isNew = !invoice?.id;
    const set = <K extends keyof SupplierInvoice>(field: K, value: SupplierInvoice[K]) =>
        setData(prev => ({ ...prev, [field]: value }));

    const sanitizeInvoice = (inv: SupplierInvoice): SupplierInvoice => ({
        ...inv,
        fiscalYearId: inv.fiscalYearId || null,
        invoiceDate: inv.invoiceDate || null,
        paymentDate: inv.paymentDate || null,
        paymentDueDate: inv.paymentDueDate || null,
        deliveryDate: inv.deliveryDate || null,
        receiveDate: inv.receiveDate || null,
        invoiceFileAttachmentId: inv.invoiceFileAttachmentId || null,
    });

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);
        if (!data.supplierId || !data.fiscalYearId) {
            setValidationError(t('validation:requiredFields'));
            setActiveTab('general');
            return;
        }
        try {
            const saved = await onSave(sanitizeInvoice(data));
            setData(saved);
        } catch {
            // parent handles error display
        }
    };

    const supplierOptions = suppliers.map(s => ({ label: s.name, value: s.id }));
    const fiscalYearOptions = fiscalYears.map(f => ({ label: f.name, value: f.id }));

    const tabs: TabConfig[] = [
        {
            key: 'general',
            label: t('screens:supplierInvoiceDialog.tabs.invoiceDetails'),
            content: (
                <>
                    {validationError && (
                        <div className="alert alert-danger">{validationError}</div>
                    )}
                    <div className="sinv-dialog__section">
                        <div className="sinv-dialog__section-title">{t('screens:supplierInvoiceDialog.sections.invoiceDetails')}</div>
                        <div className="sinv-dialog__row">
                            <div className="sinv-dialog__col sinv-dialog__col--wide">
                                <SelectInput
                                    label={t('screens:supplierInvoiceDialog.fields.supplier')}
                                    value={data.supplierId}
                                    options={supplierOptions}
                                    required
                                    onChange={v => set('supplierId', v)}
                                />
                            </div>
                            <div className="sinv-dialog__col">
                                <SelectInput
                                    label={t('screens:supplierInvoiceDialog.fields.fiscalYear')}
                                    value={data.fiscalYearId ?? ''}
                                    options={fiscalYearOptions}
                                    required
                                    onChange={v => set('fiscalYearId', v)}
                                />
                            </div>
                        </div>
                        <div className="sinv-dialog__row">
                            <div className="sinv-dialog__col">
                                <div className="mb-3">
                                    <label className="form-label">{t('screens:supplierInvoiceDialog.fields.invoiceDate')}</label>
                                    <input type="date" className="form-control" value={data.invoiceDate ?? ''} onChange={e => set('invoiceDate', e.target.value)} />
                                </div>
                            </div>
                            <div className="sinv-dialog__col">
                                <div className="mb-3">
                                    <label className="form-label">{t('screens:supplierInvoiceDialog.fields.paymentDueDate')}</label>
                                    <input type="date" className="form-control" value={data.paymentDueDate ?? ''} onChange={e => set('paymentDueDate', e.target.value)} />
                                </div>
                            </div>
                            <div className="sinv-dialog__col">
                                <div className="mb-3">
                                    <label className="form-label">{t('screens:supplierInvoiceDialog.fields.deliveryDate')}</label>
                                    <input type="date" className="form-control" value={data.deliveryDate ?? ''} onChange={e => set('deliveryDate', e.target.value)} />
                                </div>
                            </div>
                            <div className="sinv-dialog__col">
                                <div className="mb-3">
                                    <label className="form-label">{t('screens:supplierInvoiceDialog.fields.receiveDate')}</label>
                                    <input type="date" className="form-control" value={data.receiveDate ?? ''} onChange={e => set('receiveDate', e.target.value)} />
                                </div>
                            </div>
                            <div className="sinv-dialog__col">
                                <div className="mb-3">
                                    <label className="form-label">{t('screens:supplierInvoiceDialog.fields.paymentDate')}</label>
                                    <input type="date" className="form-control" value={data.paymentDate ?? ''} onChange={e => set('paymentDate', e.target.value)} />
                                </div>
                            </div>
                        </div>
                        <div className="sinv-dialog__row">
                            <div className="sinv-dialog__col">
                                <TextInput label={t('screens:supplierInvoiceDialog.fields.invoiceNumber')} value={data.invoiceNumber ?? ''} onChange={v => set('invoiceNumber', v)} />
                            </div>
                            <div className="sinv-dialog__col">
                                <TextInput label={t('screens:supplierInvoiceDialog.fields.reference')} value={data.reference ?? ''} onChange={v => set('reference', v)} />
                            </div>
                            <div className="sinv-dialog__col">
                                <TextInput label={t('screens:supplierInvoiceDialog.fields.supplierReference')} value={data.supplierReference ?? ''} onChange={v => set('supplierReference', v)} />
                            </div>
                            <div className="sinv-dialog__col">
                                <SelectInput
                                    label={t('screens:supplierInvoiceDialog.fields.paymentStatus')}
                                    value={data.isPaid ? 'true' : 'false'}
                                    options={[
                                        { label: t('common:unpaid'), value: 'false' },
                                        { label: t('common:paid'), value: 'true' },
                                    ]}
                                    onChange={v => set('isPaid', v === 'true')}
                                />
                            </div>
                        </div>
                        <div className="sinv-dialog__row">
                            <div className="sinv-dialog__col sinv-dialog__col--full">
                                <div className="mb-3">
                                    <label className="form-label">{t('screens:supplierInvoiceDialog.fields.comments')}</label>
                                    <textarea className="form-control" rows={2} value={data.comments ?? ''} onChange={e => set('comments', e.target.value)} />
                                </div>
                            </div>
                        </div>
                       
                    </div>
                    <div className="sinv-dialog__section">
                        <div className="sinv-dialog__section-title">{t('screens:supplierInvoiceDialog.sections.totals')}</div>
                        <div className="sinv-dialog__row">
                            <div className="sinv-dialog__col">
                                <NumberInput
                                    label={t('screens:supplierInvoiceDialog.fields.totalExVat')}
                                    value={data.totalAmountExVat}
                                    min={0}
                                    decimals={2}
                                    onChange={v => set('totalAmountExVat', v)}
                                />
                            </div>
                            <div className="sinv-dialog__col">
                                <NumberInput
                                    label={t('screens:supplierInvoiceDialog.fields.totalIncVat')}
                                    value={data.totalAmountIncVat}
                                    min={0}
                                    decimals={2}
                                    onChange={v => set('totalAmountIncVat', v)}
                                />
                            </div>
                            {data.isMissedPayment && (
                                <div className="sinv-dialog__col">
                                    <NumberInput
                                        label={t('screens:supplierInvoiceDialog.fields.originalAmountIncVat')}
                                        value={data.originalInvoiceAmountIncVat ?? 0}
                                        min={0} 
                                        decimals={2}
                                        onChange={v => set('originalInvoiceAmountIncVat', v)}
                                    />
                                </div>
                            )}
                            
                        </div>
                        <div className="sinv-dialog__row">
                            <div className="sinv-dialog__col">
                                <div className="mb-3 form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="sinv-isVatMoved"
                                        checked={data.isVatMoved ?? false}
                                        onChange={e => set('isVatMoved', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="sinv-isVatMoved">
                                        {t('screens:supplierInvoiceDialog.fields.isVatMoved')}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="sinv-dialog__row">
                            <div className="sinv-dialog__col">
                                <div className="mb-3 form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="sinv-isMissedPayment"
                                        checked={data.isMissedPayment ?? false}
                                        onChange={e => set('isMissedPayment', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="sinv-isMissedPayment">
                                        {t('screens:supplierInvoiceDialog.fields.isMissedPayment')}
                                    </label>
                                </div>
                            </div>
                            
                        </div>
                    </div>
                </>
            ),
        },
        {
            key: 'attachments',
            label: t('screens:supplierInvoiceDialog.tabs.attachments'),
            content: <AttachmentsPanel parentRecordId={data.id || ''} />,
        },
    ].filter(tab => !!data.id || tab.key === 'general');

    const costLedgerOptions = [
        { label: t('screens:supplierInvoiceDialog.bookDialog.selectCostLedger'), value: '' },
        ...ledgers
            .filter(l => l.type === LedgerType.Costs)
            .map(l => ({ label: `[${l.code}] ${l.name}`, value: l.id })),
    ];

    return (
        <>
            <ModalDialog
                open={open}
                title={isNew ? t('screens:supplierInvoiceDialog.newInvoice') : t('screens:supplierInvoiceDialog.editInvoice')}
                subtitle={!isNew && data.reference ? data.reference : undefined}
                onClose={onClose}
                onSubmit={handleSave}
                width={840}
                bodyVariant="tabs"
                footerLeft={!isNew ? (
                    <>
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={data.isBooked}
                            title={data.isBooked ? t('screens:supplierInvoiceDialog.actions.deleteDisabledBooked') : undefined}
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            <FaTrash style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.actions.delete')}
                        </button>
                        {!data.isBooked && (
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setShowBookConfirm(true)}>
                                <FaBook style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.actions.book')}
                            </button>
                        )}
                        {!data.isPaid && data.isBooked && (
                            <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                onClick={() => {
                                    setBookPaymentDate(new Date().toISOString().split('T')[0]);
                                    setShowBookPaymentConfirm(true);
                                }}
                            >
                                <FaMoneyBillWave style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.actions.bookPayment')}
                            </button>
                        )}
                        {data.isBooked && (
                            <button
                                type="button"
                                className="btn btn-outline-warning btn-sm"
                                onClick={() => setShowUnbookConfirm(true)}
                            >
                                <FaUndo style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.actions.unbook')}
                            </button>
                        )}
                    </>
                ) : undefined}
                footerRight={
                    <>
                        <button type="submit" className="btn btn-primary me-2">
                            <FaSave style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.actions.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <FaTimes style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.actions.close')}
                        </button>
                    </>
                }
            >
                <GenericVerticalTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
            </ModalDialog>

            <PopupDialog
                open={showDeleteConfirm}
                title={t('screens:supplierInvoiceDialog.deleteDialog.title')}
                onClose={() => setShowDeleteConfirm(false)}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDeleteConfirm(false)}>
                            {t('common:cancel')}
                        </button>
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(data)}>
                            <FaTrash style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.deleteDialog.confirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:supplierInvoiceDialog.deleteDialog.message')}</p>
            </PopupDialog>
            <PopupDialog
                open={showBookConfirm}
                title={t('screens:supplierInvoiceDialog.bookDialog.title')}
                onClose={() => setShowBookConfirm(false)}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowBookConfirm(false)}>
                            {t('common:cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={!bookCostLedgerId}
                            onClick={async () => {
                                try { await onBook(data, bookCostLedgerId); setData(prev => ({ ...prev, isBooked: true })); } catch {}
                                setShowBookConfirm(false);
                            }}
                        >
                            <FaBook style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.bookDialog.confirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:supplierInvoiceDialog.bookDialog.message')}</p>
                <div className="mb-3">
                    <label className="form-label">{t('screens:supplierInvoiceDialog.bookDialog.costLedger')}</label>
                    <select
                        className="form-select"
                        value={bookCostLedgerId}
                        onChange={e => setBookCostLedgerId(e.target.value)}
                    >
                        {costLedgerOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </PopupDialog>
            <PopupDialog
                open={showBookPaymentConfirm}
                title={t('screens:supplierInvoiceDialog.bookPaymentDialog.title')}
                onClose={() => setShowBookPaymentConfirm(false)}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowBookPaymentConfirm(false)}>
                            {t('common:cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-success btn-sm"
                            disabled={!bookPaymentDate || !bookPaymentBankAccountId}
                            onClick={async () => {
                                try { await onBookPayment(data, bookPaymentDate, bookPaymentBankAccountId); setData(prev => ({ ...prev, isPaid: true })); } catch {}
                                setShowBookPaymentConfirm(false);
                            }}
                        >
                            <FaMoneyBillWave style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.bookPaymentDialog.confirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:supplierInvoiceDialog.bookPaymentDialog.message')}</p>
                <div className="mb-3">
                    <label className="form-label">{t('screens:supplierInvoiceDialog.bookPaymentDialog.bankAccount')}</label>
                    <select
                        className="form-select"
                        value={bookPaymentBankAccountId}
                        onChange={e => setBookPaymentBankAccountId(e.target.value)}
                    >
                        <option value="">{t('screens:supplierInvoiceDialog.bookPaymentDialog.selectBankAccount')}</option>
                        {bankAccounts.map(ba => (
                            <option key={ba.id} value={ba.id}>
                                {ba.name}{ba.ibanAccountNumber ? ` — ${ba.ibanAccountNumber}` : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="mb-3">
                    <label className="form-label">{t('screens:supplierInvoiceDialog.bookPaymentDialog.paymentDate')}</label>
                    <input
                        type="date"
                        className="form-control"
                        value={bookPaymentDate}
                        onChange={e => setBookPaymentDate(e.target.value)}
                    />
                </div>
            </PopupDialog>
            <PopupDialog
                open={showUnbookConfirm}
                title={t('screens:supplierInvoiceDialog.unbookDialog.title')}
                onClose={() => setShowUnbookConfirm(false)}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowUnbookConfirm(false)}>
                            {t('common:cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-warning btn-sm"
                            onClick={async () => {
                                try {
                                    await onUnbook(data);
                                    setData(prev => ({ ...prev, isBooked: false, bookedAt: null, isPaid: false, paymentDate: null }));
                                } catch {}
                                setShowUnbookConfirm(false);
                            }}
                        >
                            <FaUndo style={{ marginRight: 5 }} />{t('screens:supplierInvoiceDialog.unbookDialog.confirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:supplierInvoiceDialog.unbookDialog.message')}</p>
            </PopupDialog>
        </>
    );
};

export default SupplierInvoiceDialog;
